import api from './api'
import {
  LoginRequest,
  AuthSessionResponse,
  UsuarioInfo,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  TotpEnrollResponse,
  TotpConfirmResponse,
  TotpStatus,
} from '@/types/auth'

const BASE_URL = '/api/v1/auth'

interface ApiEnvelope<T> {
  success: boolean
  message: string
  data: T
}

// Backend já retorna o shape novo de AuthSessionResponse (sem tokens).
interface BffSessionResponse {
  usuarioId: string
  email: string
  nome: string
  roles: string[]
  expiresIn: number
  tokenType: string
}

interface BffUserInfo {
  usuarioId: string
  email: string
  nome: string
  primeiroNome?: string
  sobrenome?: string
  roles: string[]
  tipoCanal?: string
  cpf?: string
  cnpj?: string
}

// Backend prefixa roles com "ROLE_" (padrão Spring Security). UI prefere puro.
const normalizeRoles = (roles: string[] | undefined | null): string[] =>
  (roles ?? []).map((r) => (r.startsWith('ROLE_') ? r.slice(5) : r).toUpperCase())

const toSessionResponse = (bff: BffSessionResponse): AuthSessionResponse => ({
  usuarioId: bff.usuarioId,
  email: bff.email,
  nome: bff.nome,
  roles: normalizeRoles(bff.roles),
  expiresIn: bff.expiresIn,
  tokenType: bff.tokenType,
})

const toUsuarioInfo = (bff: BffUserInfo): UsuarioInfo => ({
  id: bff.usuarioId,
  nome: bff.nome,
  email: bff.email,
  tipoCanal: bff.tipoCanal ?? '',
  permissoes: normalizeRoles(bff.roles),
  ativo: true,
})

async function postEnvelope<T>(url: string, body: unknown): Promise<T> {
  const envelope = (await api.post(url, body)) as unknown as ApiEnvelope<T>
  return envelope.data
}

async function getEnvelope<T>(url: string): Promise<T> {
  const envelope = (await api.get(url)) as unknown as ApiEnvelope<T>
  return envelope.data
}

export const authService = {
  /**
   * Login. O backend emite cookies HttpOnly de sessão (access + refresh)
   * via Set-Cookie. Não há tokens no body da resposta.
   */
  login: async (credentials: LoginRequest): Promise<AuthSessionResponse> => {
    const bff = await postEnvelope<BffSessionResponse>(`${BASE_URL}/login`, credentials)
    return toSessionResponse(bff)
  },

  /**
   * Inicia o cadastro de 2FA. Revalida a senha e retorna o QR Code + secret.
   * Não cria sessão — só após o confirm com o primeiro código válido.
   */
  totpEnroll: async (credentials: LoginRequest): Promise<TotpEnrollResponse> => {
    return postEnvelope<TotpEnrollResponse>(`${BASE_URL}/totp/enroll`, {
      email: credentials.email,
      senha: credentials.senha,
    })
  },

  /**
   * Conclui o cadastro de 2FA: valida o primeiro código e, em caso de sucesso,
   * o backend habilita o 2FA, gera os códigos de recuperação e emite a sessão.
   */
  totpConfirm: async (
    credentials: LoginRequest,
    setupToken: string,
    code: string,
  ): Promise<TotpConfirmResponse> => {
    const bff = await postEnvelope<{ session: BffSessionResponse; recoveryCodes: string[] }>(
      `${BASE_URL}/totp/confirm`,
      { email: credentials.email, senha: credentials.senha, setupToken, code },
    )
    return { session: toSessionResponse(bff.session), recoveryCodes: bff.recoveryCodes }
  },

  /** Status do 2FA (ativo + saldo de códigos de recuperação) — autenticado. */
  totpStatus: async (): Promise<TotpStatus> => {
    return getEnvelope<TotpStatus>(`${BASE_URL}/totp/status`)
  },

  /** Regenera os códigos de recuperação (invalida os antigos) — autenticado. */
  regenerateRecoveryCodes: async (): Promise<string[]> => {
    const r = await postEnvelope<{ codes: string[] }>(
      `${BASE_URL}/totp/recovery-codes/regenerate`,
      {},
    )
    return r.codes
  },

  /**
   * Logout. O backend revoga o refresh_token no Keycloak e limpa
   * os cookies via Set-Cookie com Max-Age=0.
   */
  logout: async (): Promise<void> => {
    try {
      await api.post(`${BASE_URL}/logout`)
    } catch {
      // Continue mesmo se a API falhar — o cookie expira no servidor.
    }
  },

  me: async (): Promise<UsuarioInfo> => {
    const bff = await getEnvelope<BffUserInfo>(`${BASE_URL}/me`)
    return toUsuarioInfo(bff)
  },

  /**
   * Refresh sem body — o refresh_token vai via cookie HttpOnly.
   */
  refresh: async (): Promise<AuthSessionResponse> => {
    const bff = await postEnvelope<BffSessionResponse>(`${BASE_URL}/refresh`, {})
    return toSessionResponse(bff)
  },

  forgotPassword: async (request: ForgotPasswordRequest): Promise<void> => {
    await api.post(`${BASE_URL}/forgot-password`, request)
  },

  resetPassword: async (request: ResetPasswordRequest): Promise<void> => {
    await api.post(`${BASE_URL}/reset-password`, request)
  },

  /** Troca a senha do usuário autenticado. O backend revalida a senha atual. */
  changePassword: async (request: ChangePasswordRequest): Promise<void> => {
    await api.post(`${BASE_URL}/change-password`, request)
  },
}
